import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppError } from "../../../../../store/userSlice";
import SmartDownloadPagination from "../../../../../components/SmartDataTable/SmartDownloadPagination";
import ErePopup from "../EreTab/ErePopup";
import EreExtensionPopUp from "../EreTab/EreExtensionPopUp";
import nodeinstance from "../../../../../nodeInstance";
import { checkAccessFn } from "../../../../../utils/checkAccess";
import {
  ERE_ANNUAL_VACATION_COLUMNS,
  ERE_ANNUAL_VACATION_CONFIG,
  ERE_ANNUAL_VACATION_SEARCHABLE_FIELDS,
  formatDate,
  renderAnnualVacationStatus,
  renderAnnualVacationActionButton,
  ANNUAL_VACATION_STATUS_TYPES
} from './EreAnnualVacationListConstants';

const EreAnnualVacationList = ({
  setTrigger,
  trigger,
}) => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);
  
  // Get token from localStorage (consider using context for better performance)
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  // Data tracking state for UI optimization
  const [dataState, setDataState] = useState(() => ({
    hasData: false,
    isLoading: true,
    totalRecords: 0
  }));

  // Popup state management
  const [popupState, setPopupState] = useState({
    ere: false,
    extension: false
  });

  const [propData, setPropData] = useState(null);

  // Data load handler to track data availability
  const handleDataLoad = useCallback((data, pagination) => {
    const dataExists = data && data.length > 0;
    setDataState({
      hasData: dataExists,
      isLoading: pagination.isLoading,
      totalRecords: pagination.totalRecords || 0
    });
    
    // Clear popup data if no data
    if (!dataExists) {
      setPropData(null);
    }
  }, []);

  // Optimized popup handlers
  const handleEreClick = useCallback((item) => {
    console.log("clicked prop data to be printed in the console", item);
    setPropData(item);
    setPopupState(prev => ({ ...prev, ere: true }));
  }, []);

  const handleExtensionClick = useCallback((item) => {
    setPropData(item);
    setPopupState(prev => ({ ...prev, extension: true }));
  }, []);

  // Close popup handlers
  const closeErePopup = useCallback(() => {
    setPopupState(prev => ({ ...prev, ere: false }));
    setPropData(null);
  }, []);

  const closeExtensionPopup = useCallback(() => {
    setPopupState(prev => ({ ...prev, extension: false }));
    setPropData(null);
  }, []);

  // Optimized custom cell renderer
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    switch (column.key) {
      case 'serial':
        return (currentPage - 1) * pageSize + rowIndex + 1;
      
      case 'visa_provider':
        return item.visa_provider || "-";
      
      case 'total_salary':
        return item.total_salary?.toString() || "-";
      
      case 'iqama_expiry_date_g':
      case 'arrival_date':
      case 'mobilization_date':
      case 'demobilization_date':
        return formatDate(item[column.key]);
      
      case 'Annual_entry_before':
        return formatDate(item.Annual_entry_before);
      
      case 'idle_aging':
        return item.idle_aging?.toString() || "-";
      
      case 'Annual_vacation_status':
        return renderAnnualVacationStatus(item);
      
      case 'action':
        return checkAccess("DEMOBILIZATION", "ere-edit") ? renderAnnualVacationActionButton(item, handleEreClick, handleExtensionClick) : null;
      
      default:
        return null;
    }
  }, [handleEreClick, handleExtensionClick, dashboardData]);

  // Optimized download handler (using SmartDownloadPagination's built-in download)
  const handleDownloadComplete = useCallback(() => {
    dispatch(setAppError({
      msg: "Excel Downloaded",
      color: "success",
    }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({
      msg: error.message || "Download failed",
    }));
  }, [dispatch]);

  // UpdateDataByDate functionality (preserved from original)
  const updateDataByDate = useCallback(async (currentData) => {
    const filteredData = currentData?.filter(
      (data) =>
        new Date() >= new Date(data?.demobilization_date) &&
        data.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ERE_ISSUED
    );

    if (filteredData && filteredData.length > 0) {
      try {
        const response = await nodeinstance.put(
          ERE_ANNUAL_VACATION_CONFIG.UPDATE_ANNUAL_VACATION_ENDPOINT,
          filteredData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (response.status === 200) {
          dispatch(
            setAppError({ 
              msg: "Data Updated Successfully", 
              color: "success" 
            })
          );
          setTrigger(!trigger);
        }
      } catch (error) {
        console.error("Error updating Annual Vacation data:", error);
        dispatch(setAppError({
          msg: error.message || "Failed to update Annual Vacation data"
        }));
      }
    }
  }, [token, dispatch, setTrigger, trigger]);

  // Handle data load with auto-update logic
  const handleDataLoadWithUpdate = useCallback((data, pagination) => {
    handleDataLoad(data, pagination);
    
    // Auto-update logic (preserved from original)
    if (data && data.length > 0) {
      updateDataByDate(data);
    }
  }, [handleDataLoad, updateDataByDate]);

  return (
    <div className="p-2">
      <SmartDownloadPagination
        // API Configuration
        apiEndpoint={ERE_ANNUAL_VACATION_CONFIG.API_ENDPOINT}
        downloadEndpoint={ERE_ANNUAL_VACATION_CONFIG.DOWNLOAD_ENDPOINT}
        token={token}
        
        // Table Configuration
        columns={ERE_ANNUAL_VACATION_COLUMNS}
        searchableFields={ERE_ANNUAL_VACATION_SEARCHABLE_FIELDS}
        layoutType="table" // Use table layout instead of grid
        enableHighlighting={true}
        
        // Pagination Configuration
        defaultPageSize={ERE_ANNUAL_VACATION_CONFIG.DEFAULT_PAGE_SIZE}
        pageSizeOptions={ERE_ANNUAL_VACATION_CONFIG.PAGE_SIZE_OPTIONS}
        
        // Download Configuration
        downloadFilename={ERE_ANNUAL_VACATION_CONFIG.DOWNLOAD_FILENAME}
        enableDownload={true}
        
        // Search Configuration
        searchPlaceholder={ERE_ANNUAL_VACATION_CONFIG.SEARCH_PLACEHOLDER}
        searchDebounceMs={ERE_ANNUAL_VACATION_CONFIG.DEBOUNCE_MS}
        
        // Trigger for cache invalidation
        trigger={trigger}
        
        // Customization
        renderCustomCell={renderCustomCell}
        
        // Event Handlers
        onDataLoad={handleDataLoadWithUpdate}
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}
        
        // Additional Props
        noDataMessage={ERE_ANNUAL_VACATION_CONFIG.NO_DATA_MESSAGE}
        className="ere-annual-vacation-table"
      />

      {/* Popups (preserved from original) */}
      {popupState.ere && propData && (
        <ErePopup
          PopUp={popupState.ere}
          setPopUp={closeErePopup}
          popUpData={propData}
          setTrigger={setTrigger}
          trigger={trigger}
          routeType="Annual_Vacation"
          isEdit={
            propData.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ON_ERE_ANNUAL_VACATION_PAYMENT_DONE ||
            propData.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ON_ERE_ANNUAL_VACATION ||
            propData.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ERE_ISSUED
          }
          onEre={propData.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ON_ERE_ANNUAL_VACATION}
          Cancelshow={
            propData.Annual_vacation_status != null ||
            propData?.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ON_ERE_ANNUAL_VACATION_PAYMENT_DONE ||
            propData?.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ON_ERE_ANNUAL_VACATION
          }
          issuedState={
            propData.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ERE_ISSUED ||
            propData.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ON_ERE_ANNUAL_VACATION
              ? false
              : true
          }
        />
      )}
      
      {popupState.extension && propData && (
        <EreExtensionPopUp
          PopUp={popupState.extension}
          setPopUp={closeExtensionPopup}
          popUpData={propData}
          setTrigger={setTrigger}
          trigger={trigger}
          isEdit={
            propData.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ERE_EXTENSION_PAYMENT_DONE ||
            propData.Annual_vacation_status === ANNUAL_VACATION_STATUS_TYPES.ON_ERE_EXTENSION_ISSUED
          }
          routeType="Annual_Vacation"
          Cancelshow={propData.Annual_vacation_status != null}
        />
      )}
    </div>
  );
};

EreAnnualVacationList.displayName = 'EreAnnualVacationList';

export default EreAnnualVacationList;
